
import React from "react";
import { i18nManager } from "../../i18n";
import Simulate from "../simulate/Simulate";
import { FormStep, Schemes, Utils } from "../../utils";
import { Svgs } from "../../constants/Images";
import { Status } from "../../constants/Status";
import { Button, Dialog, Dropdown, NoseurObject, NoseurRawElement, useSubscription } from "@rivtn/noseur";
import { PaymentMethod } from "../../constants/PaymentMethod";
import { ITransaction, ITransactionCharge, PloutousService, SanitizedResponse, WalletUssdBankProcessorOrMobileMoneyOperator } from "../../services";

export interface WalletProps {
    businessId: string;
    transaction: ITransaction;
    paymentSuccessful: () => void;
    loadingState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => void;
}

function Wallet(props: WalletProps) {

    const transaction = props.transaction;
    const [_, setLoading] = props.loadingState;
    const labels = i18nManager.Labels.method.wallet;
    const [walletUrl, setWalletUrl] = React.useState("");
    const ploutousService = PloutousService.getInstance();
    const containerForWallet = React.useRef<HTMLDivElement>(null);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [serviceErrors, setServiceErrors] = React.useState<string[]>([]);
    const [chargeData, setChargeData] = React.useState<NoseurObject<any>>();
    const [maximizeWalletView, setMaximizeWalletView] = React.useState(false);
    const [serviceWarnings, setServiceWarnings] = React.useState<string[]>([]);
    const [serviceSuccesses, setServiceSuccesses] = React.useState<string[]>([]);
    const [showConfirmButton, setShowConfirmButton] = React.useState<boolean>(true);
    const [confirmingPayment, setConfirmingPayment] = React.useState<boolean>(false);
    const changePaymentFormStyle = useSubscription("MOBILE.COMPOUND.PAYMENT_FORM.STYLE");
    const changeMainPanelHeight = useSubscription("MOBILE.COMPOUND.MAIN_PANEL.STYLE.HEIGHT");
    const [step, setStep] = React.useState<FormStep.SELECT_WALLET | FormStep.CONFIRM_PAYMENT>(FormStep.SELECT_WALLET);
    const countryAndCurrency = i18nManager.CountriesAndCurrencies.find(l => l.value === transaction.charge_country_code);
    const processors: WalletUssdBankProcessorOrMobileMoneyOperator[] = transaction.payment_options.find(p => p.method === PaymentMethod.WALLET)?.wallet_processors ?? [];
    const [selectedWalletUssdBankProcessorOrMobileMoneyOperator, setSelectedWalletUssdBankProcessorOrMobileMoneyOperator] = React.useState<WalletUssdBankProcessorOrMobileMoneyOperator>();

    React.useEffect(loadRawApis, []);
    React.useEffect(() => {
        changeMainPanelHeight(step === FormStep.CONFIRM_PAYMENT ? "100%" : "auto");
        changePaymentFormStyle(step === FormStep.CONFIRM_PAYMENT ? { width: "100%", paddingBottom: "10px" } : { width: "400px", paddingBottom: "60px" });

        return () => {
            changeMainPanelHeight("auto");
            changePaymentFormStyle({ width: "400px", paddingBottom: "60px" });
        };
    }, [step]);

    if (serviceSuccesses.length) {
        return Utils.buildPaymentCompletedView(transaction, serviceSuccesses);
    }

    if (serviceWarnings.length) {
        return Utils.buildPartialPaymentView(transaction, serviceWarnings, () => {
            setServiceWarnings([]);
            setStep(FormStep.SELECT_WALLET);
        }, () => {
            setServiceWarnings([]);
            setStep(FormStep.CONFIRM_PAYMENT);
        });
    }

    if (serviceErrors.length) {
        return Utils.buildPaymentErrorView(transaction, serviceErrors, () => {
            setConfirmingPayment(false);
            setStep(FormStep.SELECT_WALLET);
            setServiceErrors([]);
        });
    }

    return (<div className="ussd credit-card wallet" style={!!walletUrl ? { margin: 0 } : {}}>
        {step === FormStep.SELECT_WALLET && (<>
            <span className="title">{labels.title}</span>
            <div className="card-info" style={{ marginTop: 30 }}>
                <span className="title">{labels.choose_wallet}</span>
                <span className="desc">{labels.desc}</span>
                <Dropdown fill scheme={Schemes.RIVTN_PLOUTOUS} placeholder={labels.select_wallet}
                    options={processors} optionMap={{
                        value: "code",
                        label: "name",
                        icon: "logo_location",
                    }} onSelectOption={(o: WalletUssdBankProcessorOrMobileMoneyOperator) => {
                        setSelectedWalletUssdBankProcessorOrMobileMoneyOperator(o);
                    }} />
            </div>
        </>)}
        {(step === FormStep.CONFIRM_PAYMENT) && (<>
            <div className="view" ref={containerForWallet} />
            <Dialog notClosable maximized={maximizeWalletView} visible={!!walletUrl} contentProps={{ className: "wallet-diag-view" }} container={containerForWallet} maximizeProps={{
                maximize: { style: { position: "fixed" } }
            }} noOverlay>
                <iframe src={walletUrl} />
                <div className="ctrls">
                    <Button iconOnly scheme={Schemes.RIVTN_PLOUTOUS} leftIcon="fa fa-maximize" onClick={() => setMaximizeWalletView(!maximizeWalletView)} />
                    <Button outlined iconOnly scheme={Schemes.RIVTN_PLOUTOUS} leftIcon="fa fa-angle-left" style={{ marginLeft: 10, backgroundColor: "white" }} onClick={() => {
                        setErrorMessages([]);
                        setConfirmingPayment(false);
                        setStep(FormStep.SELECT_WALLET);
                        setSelectedWalletUssdBankProcessorOrMobileMoneyOperator(undefined);
                    }} />
                </div>
            </Dialog>
            {(chargeData?.processor === "SIM") && (<Simulate currency={countryAndCurrency?.currency_sign} pages={{
                one: {
                    inputType: "AMOUNT",
                    defaultInput: `${(transaction.amount/ Utils.AMOUNT_UNIT)}`,
                    action: async (simulate: "SUCCESS" | "FAILURE", amount?: string) => {
                        try {
                            const payload = {
                                simulate,
                                amount: parseFloat(amount ?? "-1"),
                            };
                            await ploutousService.simulateChargePayment(chargeData.charge_data, payload);
                            Utils.toastSuccess(i18nManager.Labels.simulator.successful);
                        } catch (error: any) {
                            PloutousService.reportError(error);
                        }
                    }
                }
            }} />)}
        </>)}
        {errorMessages.map((errorMessage) => <span key={errorMessage} className="err">{errorMessage}</span>)}
        {(showConfirmButton) && (<Button raised fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay" style={step === FormStep.CONFIRM_PAYMENT ? { margin: "10px 0px" } : undefined}
            text={step === FormStep.SELECT_WALLET ? i18nManager.Labels.common.next : i18nManager.Labels.common.confirm_payment}
            {...(confirmingPayment ? Utils.BUTTON_LOADING_PROPS : {})} disabled={!((step === FormStep.SELECT_WALLET && selectedWalletUssdBankProcessorOrMobileMoneyOperator) || (step === FormStep.CONFIRM_PAYMENT)) || confirmingPayment}
            onClick={proceedToPay} />)}
    </div>);

    async function proceedToPay() {
        setLoading(true);
        setErrorMessages([]);
        if (step === FormStep.SELECT_WALLET) {
            ploutousService.initiateCharge(transaction, props.businessId, PaymentMethod.WALLET, { wallet_code: selectedWalletUssdBankProcessorOrMobileMoneyOperator?.code }).then(({ sanitized }: SanitizedResponse<ITransactionCharge>) => {
                setWalletUrl(sanitized.result.wallet_url);
                setChargeData({ ...sanitized.extra_data, processor: sanitized.processor });
                setStep(FormStep.CONFIRM_PAYMENT);
                setShowConfirmButton(false);
            }).catch((error: any) => {
                setServiceErrors(error.errors ?? [error.message]);
            }).finally(() => {
                setLoading(false);
            });
            return;
        }
        confirmPayment();
    }

    async function confirmPayment(showModal: boolean = true) {
        if (showModal) setLoading(true);
        props.fetchTransaction((result?: ITransaction, error?: any) => {
            setLoading(false);
            if (error) {
                setServiceErrors(error.errors ?? [error.message]);
                return;
            }
            if ((result?.status ?? 0) === Status.TRANSACTION_PARTIALLY_PAID) {
                setServiceWarnings([i18nManager.Labels.common.partially_paid]);
                return;
            } else if ((result?.status ?? 0) > Status.TRANSACTION_FULLY_PAID_PENDING_ACKNOWLEDGEMENT) {
                props.paymentSuccessful();
                setServiceSuccesses([i18nManager.Labels.common.thank_you, i18nManager.Labels.common.payment_successful]);
                return;
            }
            if (!confirmingPayment) setConfirmingPayment(true);
            setTimeout(() => confirmPayment(false), 2000);
            console.log("Initiate web socket connection to track payment completion");
            // TODO in the API service implements logic to fetch every 2 seconds 5 times from processor once a verify transaction is received
            // also for double webhook log it to be able to detect duplicate payments from processor
        });
    }

    function loadRawApis() {
        if (window.addEventListener) {
            window.addEventListener("message", onMessage, false);
        } else if ((window as any).attachEvent) {
            (window as any).attachEvent("onmessage", onMessage, false);
        }

        function onMessage(event: any) {
            // Check sender origin to be trusted, use list to set trusted urls
            const data = event.data;
            if (/*event.origin !== ploutousService.baseUrl || */data.operation != "confirm_payment") return;
            console.log("WALLET.WINDOW.MESSAGE", data.payload);
            if (data.payload.status === "success") {
                setShowConfirmButton(true);
                confirmPayment();
            } else {
                setServiceErrors([labels.payment_failed_try_again]);
            }
        }
    }

}

export default Wallet;